<script setup lang="ts">
  import { FloatingPlacement, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import type { StayCriteria } from '@/entities/searchCriteria';
  import { Field, FieldSize, type SearchFormGoal } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import DateFromDropdown from './DateFromDropdown.vue';
  import { STAY_DATES_TEXTS } from '../config/texts';
  import { useStayDates } from '../model/useStayDates';
  import { useStayDatesTracking } from '../model/useStayDatesTracking';

  interface StayDatesFieldProps {
    modelValue: StayCriteria;
    size?: `${FieldSize}`;
    loading?: boolean;
    disabled?: boolean;
    submitElement?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<StayDatesFieldProps>(), {
    size: FieldSize.M,
    loading: false,
    disabled: false,
    submitElement: null,
  });

  interface StayDatesFieldEmits {
    (e: 'update:modelValue', value: StayCriteria): void;
    (e: 'manual'): void;
    (e: 'track', goal: SearchFormGoal): void;
  }

  const emit = defineEmits<StayDatesFieldEmits>();

  const model = computed<StayCriteria>({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const stayDates = useStayDates({ model });
  const tracking = useStayDatesTracking({ stayDates, track: (goal) => emit('track', goal) });
  const rootRef = ref<HTMLElement | null>(null);

  // С одним заездом «Найти» сама держит календарь открытым (validate). Закрытие по её mousedown дало бы
  // мигание календаря и лишние цели close/open и вторую ошибку «нет выезда».
  const isSubmitWithPendingCheckIn = (target: Node): boolean =>
    Boolean(stayDates.pendingCheckIn.value && props.submitElement?.contains(target));

  useOutsidePointer(rootRef, {
    active: stayDates.controlActivated,
    handler: tracking.close,
    shouldIgnore: isSubmitWithPendingCheckIn,
  });

  const onDayClick = (iso: string): void => {
    if (tracking.clickDate(iso)) {
      emit('manual');
    }
  };

  const onSelectFlexible = (daysFlexible: number): void => {
    tracking.setFlexible(daysFlexible);
    emit('manual');
  };

  defineExpose({
    validate: (): boolean => tracking.validate(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="stay-dates-field"
    data-testid="stay-dates-field"
  >
    <DateFromDropdown
      range
      with-exact-option
      :placement="FloatingPlacement.BottomStart"
      :range-value="stayDates.calendarValue.value"
      :flexible="stayDates.stay.value.flexible"
      :min-date="stayDates.minDate.value"
      :max-date="stayDates.maxDate.value"
      :disabled-from-date="stayDates.disabledFromDate.value"
      :visible="stayDates.controlActivated.value"
      @day-click="onDayClick"
      @day-hover="stayDates.hoverDate"
      @select-flexible="onSelectFlexible"
      @close="tracking.close"
    >
      <template #reference>
        <Field
          :label="STAY_DATES_TEXTS.fieldLabel"
          :active="stayDates.controlActivated.value"
          :error="stayDates.isInvalid.value"
          :loading="loading"
          :disabled="disabled"
          :size="size"
          value-testid="stay-dates-value"
          @click="tracking.toggle"
        >
          <template #value>
            <ClientOnly>
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
              >
                {{ stayDates.valueLabel.value }}
                <span
                  v-if="stayDates.hoverNightsLabel.value"
                  :class="[
                    'stay-dates-field__hover-nights',
                    { 'stay-dates-field__hover-nights_error': stayDates.isInvalid.value },
                  ]"
                  data-testid="stay-dates-hover-nights"
                >
                  · {{ stayDates.hoverNightsLabel.value }}
                </span>
              </Typo>
            </ClientOnly>
          </template>
        </Field>
      </template>
    </DateFromDropdown>
  </div>
</template>

<style scoped lang="scss">
  .stay-dates-field {
    position: relative;
    width: 100%;

    &__hover-nights {
      color: var(--color-text-secondary);

      &_error {
        color: inherit;
      }
    }
  }
</style>
